import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { apiFetch } from '@/lib/api';
import { formatCurrency, formatPercent, formatCount } from '@/lib/format';
import { StoreMultiSelect, collectStoreIds } from '@/components/StoreMultiSelect';
import { MonthPicker } from '@/components/MonthPicker';
import { useScopeStore, findNodeInTree } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { PublishGateBanner } from '@/components/publish/PublishGateBanner';
import type { PublishGateInfo } from '@/lib/types-publish-gate';

type Store = { store_id: string; store_name: string };

type ComparisonResponse = {
  period: { start: string; end: string };
  stores: Store[];
  series: Array<Record<string, unknown>>;
  publish_gate?: PublishGateInfo;
};

const STORE_COLORS = ['#C91F28', '#F05A28', '#F2B705', '#6E80A8', '#1A1A1A', '#8D8D8D'];

const monthsBackStart = (n: number): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const currentMonthStart = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const presets = [
  { label: '直近3ヶ月', months: 3 },
  { label: '直近6ヶ月', months: 6 },
  { label: '直近12ヶ月', months: 12 },
  { label: '直近24ヶ月', months: 24 },
];

// 選択可能な指標（左：キーサフィックス、右：日本語ラベル）
const METRICS = [
  { key: 'total_sales',          label: '売上高（税抜）',    format: 'currency' as const, chart: 'bar'  },
  { key: 'customer_count',       label: '来客数',            format: 'count' as const,    chart: 'bar'  },
  { key: 'avg_price',            label: '平均単価（税抜）',  format: 'currency' as const, chart: 'line' },
  { key: 'cost_rate',            label: '原価率',            format: 'percent' as const,  chart: 'line' },
  { key: 'gross_margin_rate',    label: '粗利率',            format: 'percent' as const,  chart: 'line' },
  { key: 'labor_rate',           label: '人件費率',          format: 'percent' as const,  chart: 'line' },
  { key: 'opex_rate',            label: '諸経費率',          format: 'percent' as const,  chart: 'line' },
  { key: 'operating_profit_rate', label: '営業利益率',       format: 'percent' as const,  chart: 'line' },
];

/** URL の stores パラメータをパース。
 *  - 未指定 (null)         : 「デフォルト」意図 → allStores 揃い次第 全選択にフォールバック
 *  - 空文字 (?stores=)     : 「明示的に空」意図 → 空のまま保持
 *  - `S-1,S-2` などの CSV : 指定 ID セット
 */
const parseStoresParam = (raw: string | null): Set<string> | null => {
  if (raw === null) return null;
  return new Set(raw.split(',').filter(Boolean));
};

export function StoreComparisonPage() {
  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-4): 複数店舗選択(URL stores=)は対象外、
  // 月軸(開始月・終了月ペア)のみ固定対象
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodStart = useScopePinStore((s) => s.pinnedPeriodStart);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);
  const [periodStart, setPeriodStart] = useState(() => (isPinned && pinnedPeriodStart ? pinnedPeriodStart : monthsBackStart(12)));
  const [periodEnd, setPeriodEnd] = useState(() => (isPinned && pinnedPeriodEnd ? pinnedPeriodEnd : currentMonthStart()));
  useEffect(() => {
    if (isPinned && pinnedPeriodStart && pinnedPeriodEnd) {
      setPeriodStart(pinnedPeriodStart);
      setPeriodEnd(pinnedPeriodEnd);
    }
  }, [isPinned, pinnedPeriodStart, pinnedPeriodEnd]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string>>(() => parseStoresParam(searchParams.get('stores')) ?? new Set());
  const [enabledMetrics, setEnabledMetrics] = useState<Set<string>>(
    () => new Set(METRICS.map((m) => m.key)),
  );
  const toggleMetric = (key: string) => setEnabledMetrics((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const { data: allStores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });

  // StoreMultiSelect (T-1001 tree化) への選択候補: NodeSwitcher で選択中のスコープ配下のみに絞り込む
  // (docs/design/v025-config-ui.md §1.1「StoreMultiSelectの選択候補はNodeSwitcherで選んだnodeの配下に絞られる」)。
  // 未選択 (全社スコープ) の場合はテナントの全ルートをそのまま渡す。
  const scopeRoots = useScopeStore((s) => s.roots);
  const scopeSelectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const scopedNodes = useMemo(() => {
    if (!scopeSelectedNodeId) return scopeRoots;
    const subtree = findNodeInTree(scopeRoots, scopeSelectedNodeId);
    return subtree ? [subtree] : scopeRoots;
  }, [scopeRoots, scopeSelectedNodeId]);
  // デフォルト選択 (URL未指定時) の候補。NodeSwitcher でスコープを絞った状態でこのページを新規に
  // 開いた場合でも、デフォルト選択がスコープ外店舗を含まないようにする (Design レビュー指摘)
  // ⚠️ bugfix (2026-09-09、本番実機報告「own_storeユーザーで店舗比較が真っ白」): scopeRoots (GET
  // /nodes) はナビゲーション用の組織ツリー全体で、own_store/node_scope ユーザーでも権限で絞り込まれ
  // ない (テナント全店舗を返す設計、apps/api/src/routes/nodes.ts listActiveNodesForTenant)。一方
  // allStores (GET /stores) は実際の閲覧権限で絞り込まれた店舗一覧。この2つの集合が異なる場合
  // (own_store ユーザーが会社全体スコープでこのページを開いた場合など)、scopedStoreIds には
  // ユーザーが実際には見れない店舗が混入し、「デフォルト選択」→「URL反映」→「URLから選択を再構築」の
  // 3つの useEffect が異なる店舗集合を行き来して収束せず、history.replaceState が高頻度に呼ばれて
  // ブラウザに強制停止され画面全体がクラッシュする不具合があった。allStores (常に権限の真実源) との
  // 積集合を取り、権限外店舗が絶対に紛れ込まないようにする。
  const allStoreIdSet = useMemo(() => new Set(allStores.map((s) => s.store_id)), [allStores]);
  const scopedStoreIds = useMemo(
    () => scopedNodes.flatMap(collectStoreIds).filter((id) => allStoreIdSet.has(id)),
    [scopedNodes, allStoreIdSet],
  );
  const scopedStoreIdsKey = scopedStoreIds.join(',');

  // 店舗集合が変わったとき: (1) URL 未指定ならスコープ配下を全選択フォールバック、(2) 存在しない store_id を除去
  const allStoresKey = allStores.map((s) => s.store_id).join(',');
  useEffect(() => {
    if (allStores.length === 0) return;
    const urlSet = parseStoresParam(searchParams.get('stores'));
    if (urlSet === null) {
      // 「デフォルト」意図: URL 未指定 → NodeSwitcher のスコープ配下を全選択
      // (scope 未取得時は従来通り allStores 全選択にフォールバック)
      setSelectedNodeIds(new Set(scopedStoreIds.length > 0 ? scopedStoreIds : allStores.map((s) => s.store_id)));
      return;
    }
    // 存在しない store_id を除去 (削除済みや誤入力に対する自衛)
    const validIds = new Set(allStores.map((s) => s.store_id));
    const cleaned = new Set(Array.from(selectedNodeIds).filter((id) => validIds.has(id)));
    if (cleaned.size !== selectedNodeIds.size) {
      setSelectedNodeIds(cleaned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStoresKey, scopedStoreIdsKey]);

  // ブラウザ戻る/進むで URL が変わった場合、選択状態を URL に同期させる
  useEffect(() => {
    const raw = searchParams.get('stores');
    const urlSet = parseStoresParam(raw);
    if (urlSet === null) return; // URL 未指定は初期化 effect の担当
    // 現状の state と URL が差分なら URL 側を優先で反映
    const current = Array.from(selectedNodeIds).sort().join(',');
    const target = Array.from(urlSet).sort().join(',');
    if (current !== target) {
      setSelectedNodeIds(urlSet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // 選択状態を URL に反映 (ブックマーク共有可能に)
  //   - 全選択と等価: パラメータを外して短く保つ (デフォルト状態)
  //   - 明示的に空 (0件): `?stores=` を残してリロード時も 0件を維持
  //   - 部分選択: `?stores=S-1,S-2`
  useEffect(() => {
    if (allStores.length === 0) return;
    const isAllSelected = selectedNodeIds.size === allStores.length
      && allStores.every((s) => selectedNodeIds.has(s.store_id));
    const next = new URLSearchParams(searchParams);
    if (isAllSelected) {
      next.delete('stores');
    } else if (selectedNodeIds.size === 0) {
      next.set('stores', ''); // 明示的空の sentinel
    } else {
      next.set('stores', Array.from(selectedNodeIds).join(','));
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeIds, allStoresKey]);

  const { data, isLoading, isError, error } = useQuery<ComparisonResponse>({
    queryKey: ['dashboard-comparison', periodStart, periodEnd],
    queryFn: () => apiFetch<ComparisonResponse>(
      `/dashboard/trends-comparison?period_start=${periodStart}&period_end=${periodEnd}`,
    ),
  });

  const stores = data?.stores ?? [];
  const series = data?.series ?? [];

  // 選択店舗にフィルタ済みの店舗リスト
  const displayStores = stores.filter((s) => selectedNodeIds.has(s.store_id));
  const storeColor = (idx: number) => STORE_COLORS[idx % STORE_COLORS.length]!;
  // 659店舗規模のテナントで店舗フィルタツリーの行ごとに findIndex (O(n)) が走らないよう、
  // インデックスを一度だけ Map 化しておく (店舗比較ページ性能改善、2026-08-11)
  const storeColorIndex = useMemo(() => {
    const map = new Map<string, number>();
    allStores.forEach((s, idx) => map.set(s.store_id, idx));
    return map;
  }, [allStores]);
  const storeColorById = useCallback(
    (storeId: string) => storeColor(storeColorIndex.get(storeId) ?? 0),
    [storeColorIndex],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-h1">店舗比較</h1>
      </div>

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-neutral-sub mb-1">期間開始</label>
            <MonthPicker aria-label="期間開始" value={periodStart} onChange={(v) => setPeriodStart(v + '-01')}
              className="px-3 py-1.5 rounded border border-neutral-line text-sm" />
          </div>
          <div>
            <label className="block text-xs text-neutral-sub mb-1">期間終了</label>
            <MonthPicker aria-label="期間終了" align="right" value={periodEnd} onChange={(v) => setPeriodEnd(v + '-01')}
              className="px-3 py-1.5 rounded border border-neutral-line text-sm" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {presets.map((p) => (
              <button key={p.label}
                onClick={() => { setPeriodStart(monthsBackStart(p.months)); setPeriodEnd(currentMonthStart()); }}
                className="min-h-[36px] px-3 py-1.5 text-xs rounded border border-neutral-line hover:bg-neutral-panel">
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* 店舗選択 (Multi-select Popover + 保存済みビュー、URL に選択状態を反映) */}
        <div>
          <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
            <label className="block text-xs text-neutral-sub">比較対象店舗</label>
            <div className="flex items-center gap-2">
              <StoreMultiSelect
                nodes={scopedNodes}
                selectedIds={selectedNodeIds}
                onChange={setSelectedNodeIds}
                colorOf={storeColorById}
              />
            </div>
          </div>
          {/* 選択済み店舗のミニチップ (視覚的な現状把握と 1クリック解除) */}
          {selectedNodeIds.size > 0 && selectedNodeIds.size <= 12 && (
            <div className="flex flex-wrap gap-1.5">
              {allStores.filter((s) => selectedNodeIds.has(s.store_id)).map((s) => (
                <button
                  key={s.store_id}
                  type="button"
                  onClick={() => {
                    const next = new Set(selectedNodeIds);
                    next.delete(s.store_id);
                    setSelectedNodeIds(next);
                  }}
                  aria-label={`${s.store_name} を選択解除`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-neutral-line bg-neutral-panel hover:border-warning-text group"
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: storeColorById(s.store_id) }} />
                  {s.store_name}
                  <span className="text-neutral-sub group-hover:text-warning-text">×</span>
                </button>
              ))}
            </div>
          )}
          {selectedNodeIds.size > 12 && (
            <div className="text-xs text-neutral-sub">
              {selectedNodeIds.size} 店舗を選択中 (一覧はドロップダウンで確認)
            </div>
          )}
          {selectedNodeIds.size === 0 && (
            <p className="text-xs text-neutral-sub">
              上のボタンから比較対象店舗を選択してください
            </p>
          )}
        </div>

        {/* メトリクス選択 */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs text-neutral-sub">表示メトリクス</label>
            <div className="flex gap-2">
              <button type="button"
                onClick={() => setEnabledMetrics(new Set(METRICS.map((m) => m.key)))}
                className="text-xs text-brand-red hover:underline">全選択</button>
              <button type="button"
                onClick={() => setEnabledMetrics(new Set())}
                className="text-xs text-neutral-sub hover:text-brand-red">全解除</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {METRICS.map((m) => {
              const on = enabledMetrics.has(m.key);
              return (
                <button key={m.key}
                  type="button"
                  onClick={() => toggleMetric(m.key)}
                  aria-pressed={on}
                  className={`px-3 py-1 text-xs rounded-full border transition ${
                    on ? 'bg-brand-red text-white border-brand-red' : 'bg-white text-neutral-sub border-neutral-line'
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {!isLoading && !isError && <PublishGateBanner publishGate={data?.publish_gate} />}

      {isLoading && <div className="text-neutral-sub">読み込み中…</div>}
      {isError && <div className="p-3 rounded bg-warning-bg text-warning-text border border-warning-border">{(error as Error).message}</div>}

      {!isLoading && !isError && series.length === 0 && (
        <div className="p-6 text-center text-neutral-sub">対象期間のデータがありません</div>
      )}

      {series.length > 0 && enabledMetrics.size === 0 && (
        <div className="p-6 text-center text-neutral-sub border border-neutral-line rounded-lg bg-white">
          表示するメトリクスを選択してください
        </div>
      )}

      {series.length > 0 && enabledMetrics.size > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {METRICS.filter((m) => enabledMetrics.has(m.key)).map((metric) => (
            <ChartCard key={metric.key} title={`${metric.label} 店舗別推移`}>
              <ResponsiveContainer width="100%" height={260}>
                {metric.chart === 'bar' ? (
                  <BarChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis dataKey="period_label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => yFmt(v, metric.format)} />
                    <Tooltip
                      formatter={(v: number, k: string) => [valFmt(v, metric.format), storeName(k, stores)]}
                    />
                    <Legend formatter={(k) => storeName(k, stores)} />
                    {displayStores.map((s) => (
                      <Bar
                        key={s.store_id}
                        dataKey={nodeDataKey(s.store_id, metric.key)}
                        fill={storeColorById(s.store_id)}
                      />
                    ))}
                  </BarChart>
                ) : (
                  <LineChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis dataKey="period_label" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => yFmt(v, metric.format)}
                      domain={metric.format === 'percent' ? ['auto', 'auto'] : undefined} />
                    <Tooltip
                      formatter={(v: number, k: string) => [valFmt(v, metric.format), storeName(k, stores)]}
                    />
                    <Legend formatter={(k) => storeName(k, stores)} />
                    {displayStores.map((s) => (
                      <Line
                        key={s.store_id}
                        type="monotone"
                        dataKey={nodeDataKey(s.store_id, metric.key)}
                        stroke={storeColorById(s.store_id)}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                )}
              </ResponsiveContainer>
            </ChartCard>
          ))}
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-neutral-line rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-line bg-neutral-panel">
        <h2 className="text-h3">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const yFmt = (v: number, fmt: 'currency' | 'percent' | 'count'): string => {
  if (fmt === 'currency') return v >= 1_000_000 ? (v / 1_000_000).toFixed(1) + '百万' : v.toLocaleString('ja-JP');
  if (fmt === 'percent') return `${(v * 100).toFixed(0)}%`;
  return v >= 1000 ? (v / 1000).toFixed(1) + '千' : String(v);
};
const valFmt = (v: number, fmt: 'currency' | 'percent' | 'count'): string => {
  if (fmt === 'currency') return formatCurrency(v);
  if (fmt === 'percent') return formatPercent(v);
  return formatCount(v);
};

/** Recharts の dataKey を "<node_id>_<metric>" 形式で組み立てる (docs/design/v025-config-ui.md
 *  §2-5-1 T-1002 対応)。store 種別ノードは backfill 後 node_id === store_id の invariant
 *  (apps/api/src/lib/scope.ts 参照) のため、バックエンド (/dashboard/trends-comparison) が
 *  既に生成している `${store_id}_${metric}` キーと値としては完全に一致する。 */
const nodeDataKey = (storeId: string, metricKey: string): string => `${storeId}_${metricKey}`;

/** dataKey "<node_id>_<metric>" → 店舗名を返す。node_id === store_id (上記) のため既存 API の
 *  store_id と前方一致で照合できる。node_id にアンダースコアが含まれるケースにも対応するため
 *  既知IDと前方一致で照合 */
const storeName = (key: string, stores: Store[]): string => {
  const matched = stores.find((s) => key.startsWith(s.store_id + '_'));
  return matched?.store_name ?? key;
};
